import type { Contact } from "@shopify-harness/sdk";
import { ShopifyHarnessApiError } from "@shopify-harness/sdk";

import { milestonesFixture } from "@/features/calendar/mock-data";
import { personasFixture } from "@/features/personas/mock-data";

import type { AppApi } from "./services";

const contactsFixture: Contact[] = [
  {
    id: "contact-1",
    shopDomain: "analytical-engine.myshopify.com",
    email: "ada@example.com",
    firstName: "Ada",
    lastName: "Lovelace",
    status: "active",
    ordersCount: 8,
    amountSpent: 124_800,
    amountSpentCurrency: "JPY",
    ltvSyncedAt: "2026-07-06T00:00:00.000Z",
    createdAt: "2026-07-01T00:00:00.000Z",
    updatedAt: "2026-07-01T00:00:00.000Z"
  },
  {
    id: "contact-2",
    shopDomain: "analytical-engine.myshopify.com",
    email: "grace@example.com",
    firstName: "Grace",
    lastName: "Hopper",
    status: "unsubscribed",
    ordersCount: 2,
    amountSpent: 18_400,
    amountSpentCurrency: "JPY",
    ltvSyncedAt: "2026-07-06T00:00:00.000Z",
    createdAt: "2026-07-02T00:00:00.000Z",
    updatedAt: "2026-07-02T00:00:00.000Z"
  },
  {
    id: "contact-3",
    shopDomain: "analytical-engine.myshopify.com",
    email: "margaret@example.com",
    firstName: "Margaret",
    lastName: "Hamilton",
    status: "active",
    ordersCount: null,
    amountSpent: null,
    amountSpentCurrency: null,
    ltvSyncedAt: null,
    createdAt: "2026-07-03T00:00:00.000Z",
    updatedAt: "2026-07-05T00:00:00.000Z"
  }
];

function stubWriteError(operation: string): ShopifyHarnessApiError {
  return new ShopifyHarnessApiError({
    status: 405,
    code: "stub_mode",
    message: `スタブモード（VITE_API_MODE=stub）では ${operation} は実行できません。Worker を起動し live モードで接続してください。`
  });
}

/**
 * バックエンド未接続で UI を確認するためのスタブ実装（VITE_API_MODE=stub）。
 * 画面側は SDK と同じ interface しか見ないため、live との切替は composition root だけで閉じる。
 */
export function createStubApi(): AppApi {
  return {
    health: async () => ({
      service: "shopify-harness-worker",
      status: "ok",
      timestamp: new Date().toISOString(),
      version: "0.1.0-stub"
    }),
    contacts: {
      list: async () => ({ contacts: [...contactsFixture], cursor: null }),
      syncLtv: async () => {
        throw stubWriteError("ltv_sync");
      }
    },
    milestones: {
      list: async (options) => ({
        milestones: milestonesFixture.filter((milestone) => {
          if (options?.category && milestone.category !== options.category) return false;
          // from/to は期間の重なりで判定する（実APIと同じ挙動）
          if (options?.from && (milestone.endedOn ?? milestone.startedOn) < options.from)
            return false;
          if (options?.to && milestone.startedOn > options.to) return false;
          return true;
        }),
        cursor: null
      }),
      create: async () => {
        throw stubWriteError("create_milestone");
      },
      update: async () => {
        throw stubWriteError("update_milestone");
      },
      delete: async () => {
        throw stubWriteError("delete_milestone");
      }
    },
    personas: {
      list: async (options) => ({
        personas: personasFixture.filter(
          (persona) => !options?.status || persona.status === options.status
        ),
        cursor: null
      }),
      get: async (personaId) => {
        const persona = personasFixture.find((entry) => entry.id === personaId);
        if (!persona) {
          throw new ShopifyHarnessApiError({
            status: 404,
            code: "not_found",
            message: "Persona not found"
          });
        }
        return persona;
      },
      upsert: async () => {
        throw stubWriteError("upsert_persona");
      },
      delete: async () => {
        throw stubWriteError("delete_persona");
      }
    }
  };
}
